import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import {
  Ban,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Loader2,
  Minus,
  PanelRightOpen,
  Pencil,
  Search,
  Undo2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Peças visuais do Gerenciamento (Claude Design, `Management.dc.html`): linha e
 * card de pendente, linha de decidido, busca, origem e ações. Estrutura
 * organizacional, SSCUI e App Fiori usam as mesmas peças.
 */

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--exed-ink-900)]";

/** Só unidade criada à mão ganha selo; o que veio do ERP é o normal e fica sem. */
export type DecisionSource = "manual";
export type SourceFilter = "all" | "erp" | "manual";

const SOURCE_STYLE: Record<
  DecisionSource,
  { label: string; className: string }
> = {
  manual: {
    label: "Manual",
    className: "bg-[rgba(8,37,76,0.08)] text-[var(--exed-ink-900)]",
  },
};

export function SourcePill({
  source,
  className,
}: {
  source: DecisionSource;
  className?: string;
}) {
  const { t } = useTranslation();
  const style = SOURCE_STYLE[source];
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold",
        style.className,
        className,
      )}
    >
      {t(style.label)}
    </span>
  );
}

export function DiagnosticBadge() {
  const { t } = useTranslation();
  return (
    <span className="shrink-0 rounded-full bg-[rgba(8,37,76,0.08)] px-2 py-0.5 text-[11px] font-semibold text-[var(--exed-ink-900)]">
      {t("From the Diagnostic")}
    </span>
  );
}

export function CodeChip({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "min-w-[44px] shrink-0 rounded bg-[#f1f3f6] px-1.5 py-0.5 text-center font-mono text-xs font-medium text-[var(--exed-ink-900)]",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Checkbox de 16px do design: borda cinza, marcado em azul-escuro. */
export function DecisionCheckbox({
  checked,
  onChange,
  label,
  labelledBy,
  disabled = false,
}: {
  checked: boolean | "indeterminate";
  onChange: (checked: boolean) => void;
  label?: string;
  labelledBy?: string;
  disabled?: boolean;
}) {
  return (
    <CheckboxPrimitive.Root
      checked={checked}
      onCheckedChange={(value) => onChange(value === true)}
      aria-label={label}
      aria-labelledby={labelledBy}
      disabled={disabled}
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px] border-[#c3cbd6] bg-white text-white",
        "data-[state=checked]:border-[var(--exed-ink-900)] data-[state=checked]:bg-[var(--exed-ink-900)]",
        "data-[state=indeterminate]:border-[var(--exed-ink-900)] data-[state=indeterminate]:bg-[var(--exed-ink-900)]",
        FOCUS,
      )}
    >
      <CheckboxPrimitive.Indicator>
        {checked === "indeterminate" ? (
          <Minus aria-hidden="true" className="h-3 w-3" strokeWidth={3} />
        ) : (
          <Check aria-hidden="true" className="h-3 w-3" strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

const ICON_BUTTON = cn(
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors [&_svg]:h-4 [&_svg]:w-4",
  FOCUS,
);
const APPROVE_TONE =
  "border border-[rgba(39,161,85,0.25)] bg-[rgba(39,161,85,0.08)] text-[#1e7d42] hover:border-[var(--exed-success)] hover:bg-[var(--exed-success)] hover:text-white";
const REJECT_TONE =
  "border border-[rgba(212,53,60,0.22)] bg-[rgba(212,53,60,0.06)] text-[#a3262c] hover:border-[var(--exed-danger)] hover:bg-[var(--exed-danger)] hover:text-white";
const GHOST_TONE =
  "text-[var(--exed-ink-500)] hover:bg-[#f1f3f6] hover:text-[var(--exed-ink-900)]";

type ButtonProps = {
  label: string;
  title?: string;
  onClick: () => void;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "title" | "type">;

export function ApproveButton({
  label,
  title,
  onClick,
  withText = false,
  className,
  ...rest
}: ButtonProps & { withText?: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? t("Approve")}
      onClick={onClick}
      className={cn(
        ICON_BUTTON,
        APPROVE_TONE,
        withText &&
          "w-auto flex-1 gap-1.5 px-3 text-xs font-semibold [&_svg]:h-3.5 [&_svg]:w-3.5",
        className,
      )}
      {...rest}
    >
      <Check aria-hidden="true" />
      {withText && t("Approve")}
    </button>
  );
}

export function OutOfScopeButton({
  label,
  title,
  onClick,
  className,
  ...rest
}: ButtonProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? t("Take out of scope")}
      onClick={onClick}
      className={cn(ICON_BUTTON, REJECT_TONE, className)}
      {...rest}
    >
      <Ban aria-hidden="true" />
    </button>
  );
}

export function GhostIconButton({
  label,
  title,
  onClick,
  icon,
  bordered = false,
  className,
  ...rest
}: ButtonProps & { icon: ReactNode; bordered?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      onClick={onClick}
      className={cn(
        ICON_BUTTON,
        GHOST_TONE,
        bordered && "border border-[var(--exed-card-border)] bg-white",
        className,
      )}
      {...rest}
    >
      {icon}
    </button>
  );
}

/** Aprovar · Fora do escopo | Editar · Detalhes — a ordem do design. */
export function PendingActions({
  approveLabel,
  outLabel,
  editLabel,
  detailsLabel,
  onApprove,
  onOut,
  onEdit,
  onDetails,
  containerProps,
}: {
  approveLabel: string;
  outLabel: string;
  editLabel?: string;
  detailsLabel: string;
  onApprove: () => void;
  onOut: () => void;
  onEdit?: () => void;
  onDetails: () => void;
  containerProps?: HTMLAttributes<HTMLDivElement> &
    Record<`data-${string}`, string>;
}) {
  const { t } = useTranslation();
  return (
    <div {...containerProps} className="flex shrink-0 items-center gap-1">
      <ApproveButton
        data-pending-action=""
        label={approveLabel}
        onClick={onApprove}
      />
      <OutOfScopeButton label={outLabel} onClick={onOut} />
      <span
        aria-hidden="true"
        className="mx-1 h-5 w-px bg-[var(--exed-card-border)]"
      />
      {onEdit && editLabel && (
        <GhostIconButton
          label={editLabel}
          title={t("Edit")}
          icon={<Pencil />}
          onClick={onEdit}
        />
      )}
      <GhostIconButton
        label={detailsLabel}
        title={t("View details")}
        icon={<PanelRightOpen />}
        onClick={onDetails}
      />
    </div>
  );
}

export interface DecisionItem {
  code: ReactNode;
  name: ReactNode;
  type: ReactNode;
  /** Só o que foge do normal ganha selo: hoje, unidade criada à mão. Extraído do ERP não tem. */
  source?: DecisionSource;
}

/** Linha de pendente: checkbox, seta (quando tem filhos), código, nome + tipo, origem, ações. */
export function DecisionRow({
  item,
  depth = 0,
  checked,
  onCheck,
  checkLabel,
  expander,
  actions,
  rowProps,
}: {
  item: DecisionItem;
  depth?: number;
  checked: boolean;
  onCheck?: (checked: boolean) => void;
  checkLabel: string;
  expander?: ExpanderProps | null;
  actions: ReactNode;
  rowProps?: HTMLAttributes<HTMLLIElement> & Record<`data-${string}`, string>;
}) {
  return (
    <li
      {...rowProps}
      data-view="list"
      style={{ paddingLeft: `${20 + depth * 30}px` }}
      className={cn(
        "flex items-center gap-2.5 border-b border-[#f0f2f5] py-2.5 pr-5 [content-visibility:auto] [contain-intrinsic-size:auto_53px]",
        checked ? "bg-[rgba(255,200,61,0.08)]" : "bg-white hover:bg-[#fafbfc]",
      )}
    >
      {onCheck ? (
        <DecisionCheckbox
          checked={checked}
          onChange={onCheck}
          label={checkLabel}
        />
      ) : (
        <span className="w-4 shrink-0" />
      )}
      <Expander expander={expander} />
      {/* Código, nome e tipo também abrem os filhos: a seta sozinha é alvo pequeno
          demais. O teclado continua pela seta, que é o botão de verdade. */}
      <div
        onClick={expander?.onToggle}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2.5",
          expander && "cursor-pointer",
        )}
      >
        <CodeChip>{item.code}</CodeChip>
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="min-w-0 break-words text-sm font-semibold text-[var(--exed-ink-900)]">
            {item.name}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--exed-ink-500)]">
            {item.type}
          </span>
        </div>
      </div>
      {item.source && <SourcePill source={item.source} />}
      {actions}
    </li>
  );
}

/** `loading`: os filhos desta linha ainda estão a caminho; a seta vira o indicador e segue clicável (fechar). */
type ExpanderProps = {
  expanded: boolean;
  onToggle: () => void;
  label: string;
  loading?: boolean;
};

/** Seta de abrir/fechar filhos; sem filhos, ocupa o mesmo espaço para alinhar. */
function Expander({ expander }: { expander?: ExpanderProps | null }) {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center">
      {expander && (
        <button
          type="button"
          aria-expanded={expander.expanded}
          aria-label={expander.label}
          onClick={expander.onToggle}
          aria-busy={expander.loading ? "true" : undefined}
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded text-[var(--exed-ink-500)] hover:bg-[#f1f3f6]",
            FOCUS,
          )}
        >
          {expander.loading ? (
            <Loader2
              aria-hidden="true"
              className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
            />
          ) : expander.expanded ? (
            <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
          )}
        </button>
      )}
    </span>
  );
}

/** Recuo de `DecidedRow` mais o espaço da seta (10 + 24): o que os decididos usam. */
const DECIDED_CHILDREN_BASE = 10 + 24;

/** Recuo dos filhos: `base` (onde cai a seta do primeiro nível) mais 30 por nível, para a linha ficar alinhada com eles. */
function childrenIndent(depth: number, base: number) {
  return { paddingLeft: `${base + depth * 30}px` };
}

/**
 * Linha provisória sob a unidade aberta enquanto os filhos chegam do servidor. `depth` é o dos filhos;
 * `base` é o recuo do nível zero (os pendentes passam o deles, com a coluna do checkbox).
 */
export function ChildrenLoading({
  depth,
  base = DECIDED_CHILDREN_BASE,
}: {
  depth: number;
  base?: number;
}) {
  const { t } = useTranslation();
  return (
    <li
      role="status"
      aria-live="polite"
      data-testid="children-loading"
      style={childrenIndent(depth, base)}
      className="flex items-center gap-2 border-b border-[#f0f2f5] py-2 pr-3 text-xs text-[var(--exed-ink-500)]"
    >
      <Loader2
        aria-hidden="true"
        className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
      />
      {t("Loading…")}
    </li>
  );
}

/** Falha ao buscar os filhos de uma unidade aberta, com nova tentativa. `depth` é o dos filhos. */
export function ChildrenError({
  depth,
  onRetry,
  base = DECIDED_CHILDREN_BASE,
}: {
  depth: number;
  onRetry: () => void;
  base?: number;
}) {
  const { t } = useTranslation();
  return (
    <li
      role="alert"
      data-testid="children-error"
      style={childrenIndent(depth, base)}
      className="flex items-center gap-2 border-b border-[#f0f2f5] py-2 pr-3 text-xs text-[var(--exed-ink-500)]"
    >
      <span>{t("Could not load the hierarchy")}</span>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        {t("Retry")}
      </Button>
    </li>
  );
}

/**
 * Linha de decidido: seta (árvore), ↩ (ou duplo check, se já executado), caminho
 * do pai na busca, código e nome, editar (quando cabe) e detalhes à direita.
 */
export function DecidedRow({
  code,
  name,
  path,
  depth = 0,
  expander,
  returnLabel,
  onReturn,
  returnDisabled = false,
  doneLabel,
  detailsLabel,
  badge,
  editLabel,
  onEdit,
  onDetails,
  detailsDisabledReason,
  rowProps,
}: {
  code: ReactNode;
  name: ReactNode;
  path?: string | null;
  depth?: number;
  expander?: ExpanderProps | null;
  returnLabel: string;
  onReturn?: () => void;
  returnDisabled?: boolean;
  /** Já executado (cadastrado no CBC / configurado): não volta, mostra o duplo check. */
  doneLabel?: string;
  detailsLabel: string;
  badge?: ReactNode;
  /** Sem ele (ou sem `editLabel`) o lápis não aparece. */
  editLabel?: string;
  onEdit?: () => void;
  onDetails?: () => void;
  /** Detalhes indisponíveis (sem extração): o botão fica desabilitado com este motivo. */
  detailsDisabledReason?: string;
  rowProps?: HTMLAttributes<HTMLLIElement> & Record<`data-${string}`, string>;
}) {
  const { t } = useTranslation();
  return (
    <li
      {...rowProps}
      style={{ paddingLeft: `${10 + depth * 30}px` }}
      className="flex items-center gap-2 border-b border-[#f0f2f5] py-2 pr-3 last:border-b-0 hover:bg-[#fafbfc]"
    >
      {doneLabel ? (
        <span
          role="img"
          aria-label={doneLabel}
          title={doneLabel}
          className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center text-[#1e7d42]"
        >
          <CheckCheck aria-hidden="true" className="h-4 w-4" />
        </span>
      ) : onReturn ? (
        <button
          type="button"
          aria-label={returnLabel}
          title={t("Return to pending")}
          onClick={onReturn}
          disabled={returnDisabled}
          className={cn(
            "inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md border border-[var(--exed-card-border)] bg-white text-[var(--exed-ink-500)] hover:bg-[rgba(255,200,61,0.1)] hover:text-[var(--exed-ink-900)] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:h-3.5 [&_svg]:w-3.5",
            FOCUS,
          )}
        >
          <Undo2 aria-hidden="true" />
        </button>
      ) : (
        <span aria-hidden="true" className="w-[30px] shrink-0" />
      )}
      {expander !== undefined && <Expander expander={expander} />}
      <div
        onClick={expander?.onToggle}
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-px",
          expander && "cursor-pointer",
        )}
      >
        {path && (
          <span className="truncate font-mono text-[10px] text-[var(--exed-ink-500)]">
            {path} ›
          </span>
        )}
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 font-mono text-xs text-[var(--exed-ink-500)]">
            {code}
          </span>
          <span className="truncate text-[13px] text-[var(--exed-ink-900)]">
            {name}
          </span>
        </div>
      </div>
      {badge}
      {onEdit && editLabel && (
        <GhostIconButton
          label={editLabel}
          title={t("Edit")}
          icon={<Pencil />}
          onClick={onEdit}
          className="h-[30px] w-[30px] [&_svg]:h-3.5 [&_svg]:w-3.5"
        />
      )}
      {onDetails && (
        <GhostIconButton
          label={detailsLabel}
          title={detailsDisabledReason ?? t("View details")}
          icon={<PanelRightOpen />}
          onClick={onDetails}
          disabled={!!detailsDisabledReason}
          className="h-[30px] w-[30px] disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:h-3.5 [&_svg]:w-3.5"
        />
      )}
    </li>
  );
}

/** Nome ausente: aviso discreto, sem repetir o código. */
/** Unidade sem descrição no ERP (a área de vendas nunca tem): o código já identifica, o nome fica um traço. */
export function MissingName() {
  const { t } = useTranslation();
  return (
    <>
      <span aria-hidden="true" className="text-[var(--exed-ink-500)]">
        —
      </span>
      <span className="sr-only">{t("name not extracted")}</span>
    </>
  );
}

/**
 * Corpo do painel de decididos: busca, lista rolável e vazios do design. A busca
 * é opcional: sem `onSearch` (a barra global da estrutura organizacional filtra
 * por fora) a caixa não é desenhada.
 */
export function DecidedPanelBody({
  search = "",
  onSearch,
  onSearchEnter,
  placeholder = "",
  searchLabel = "",
  filtering = Boolean(search),
  hasItems,
  hasMatches,
  bare = false,
  footer,
  children,
}: {
  /** Rodapé fixo do painel (paginação). */
  footer?: ReactNode;
  /** Conteúdo já estruturado (grupos): sem a `<ul>` em volta. */
  bare?: boolean;
  search?: string;
  onSearch?: (value: string) => void;
  /** Enter na busca: aplica sem a espera de 3 s. */
  onSearchEnter?: () => void;
  placeholder?: string;
  searchLabel?: string;
  /** Há filtro ativo (busca de fora, por exemplo): o vazio vira "No matches.". */
  filtering?: boolean;
  hasItems: boolean;
  hasMatches: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      {onSearch && (
        <div className="px-4 pb-2.5">
          <div className="flex h-[34px] items-center gap-2 rounded-md border border-[var(--exed-card-border)] bg-white px-2.5 text-[var(--exed-ink-500)]">
            <Search aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            <input
              type="search"
              aria-label={searchLabel}
              placeholder={placeholder}
              value={search}
              onChange={(event) => onSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") onSearchEnter?.();
              }}
              className="min-w-0 flex-1 bg-transparent text-[13px] text-[var(--exed-ink-900)] outline-none placeholder:text-[var(--exed-ink-500)]"
            />
          </div>
        </div>
      )}
      <div className="max-h-[380px] overflow-y-auto border-t border-[#f0f2f5]">
        {hasMatches ? (
          bare ? (
            children
          ) : (
            <ul>{children}</ul>
          )
        ) : (
          <p className="px-4 py-5 text-center text-[13px] text-[var(--exed-ink-500)]">
            {hasItems || filtering ? t("No matches.") : t("Nothing here yet.")}
          </p>
        )}
        {footer}
      </div>
    </>
  );
}

/** Busca do painel de pendentes. `onEnter` (o Enter) aplica a busca sem a espera de 3 s. */
export function FilterSearch({
  value,
  onChange,
  label,
  onEnter,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  onEnter?: () => void;
}) {
  return (
    <div className="flex h-9 min-w-0 flex-[1_1_220px] items-center gap-2 rounded-md border border-[var(--exed-card-border)] bg-white px-3 text-[var(--exed-ink-500)]">
      <Search aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <input
        type="search"
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") onEnter?.();
        }}
        className="min-w-0 flex-1 bg-transparent text-[13px] text-[var(--exed-ink-900)] outline-none placeholder:text-[var(--exed-ink-500)]"
      />
    </div>
  );
}

export const FILTER_SELECT_CLASS =
  "h-9 cursor-pointer rounded-md border border-[var(--exed-card-border)] bg-white px-2.5 text-[13px] text-[var(--exed-ink-900)]";
export const FILTER_ROW_CLASS =
  "flex flex-wrap items-center gap-2 border-b border-[var(--exed-card-border)] px-5 py-3.5";

/** Origem segmentada: All · Extracted · Manual. */
export function SourceSegment({
  value,
  onChange,
}: {
  value: SourceFilter;
  onChange: (value: SourceFilter) => void;
}) {
  const { t } = useTranslation();
  const options: Array<[SourceFilter, string]> = [
    ["all", "All"],
    ["erp", "Extracted"],
    ["manual", "Manual"],
  ];
  return (
    <div
      role="group"
      aria-label={t("Origin")}
      className="flex gap-0.5 rounded-md border border-[var(--exed-card-border)] bg-[#f7f7f7] p-0.5"
    >
      {options.map(([key, label]) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(key)}
            className={cn(
              "h-[30px] rounded px-3 text-[13px] font-medium",
              FOCUS,
              active
                ? "bg-white text-[var(--exed-ink-900)] shadow-[0_1px_2px_rgba(8,37,76,0.12)]"
                : "text-[var(--exed-ink-500)] hover:text-[var(--exed-ink-900)]",
            )}
          >
            {t(label)}
          </button>
        );
      })}
    </div>
  );
}

export function EmptyPending() {
  const { t } = useTranslation();
  return (
    <p className="px-5 py-10 text-center text-sm text-[var(--exed-ink-500)]">
      {t("No pending items match the filters.")}
    </p>
  );
}
